// @flow

import React, { Fragment } from 'react';
import ImageDrop from 'components/ImageDrop';
import ImageCrop from 'components/ImageCrop';
import type { ShaderSettings } from 'components/shaders/LineShader';
import type { DroppedImage } from 'components/ImageDrop';
import ReactGA from 'react-ga';
import type { Resolution } from 'types/Settings';
import { initialPreset } from './../PresetSwitcher';
import SideBar from './../SideBar';

import {
  Title,
  Wrap,
  ViewerWrap,
  BrandImageViewerStyled,
  IconButton,
} from './Editor.styled';

type Props = {
  preset: any,
};

type State = {
  settings: {
    blend: string,
    brightness: number,
    contrast: number,
    thickness: number,
    resolution: Resolution,
  },
  image: DroppedImage,
  finalResolution: Resolution,
  croppedImageSrc: string,
};

class Editor extends React.Component<Props, State> {
  state = {
    settings: initialPreset.defaults,
    hidden: initialPreset.hidden,
    croppedImageSrc: null,
  };

  setStateSettings = newSettings => {
    this.setState(({ settings }) => ({
      settings: { ...settings, ...newSettings },
    }));
  };

  handleImageChange = (image: DroppedImage) => {
    if (image.size) {
      ReactGA.event({
        category: 'Image',
        action: 'Load',
      });

      const finalResolution = {
        ...image.size,
        ratio: image.size.width / image.size.height,
      };
      this.setState({ image, finalResolution, croppedImageSrc: null });
    } else {
      this.setState({ image, croppedImageSrc: null });
    }
  };

  handleResolutionReset = () => {
    const { image } = this.state;
    if (image && image.size) {
      const finalResolution = {
        ...image.size,
        ratio: image.size.width / image.size.height,
      };
      this.setState({ finalResolution });
    }
  };

  handleShaderSettingsChanged = (settings: ShaderSettings) => {
    this.setStateSettings({ ...settings });
  };

  handleResolutionChange = (resolution: Resolution) => {
    this.setState({ finalResolution: resolution });
  };

  handleSaveClick = () => {
    if (this.imageRef) {
      this.imageRef.current.saveImage();
    }
  };

  handleImageCrop = (imageSrc: string) => {
    const { image } = this.state;
    image.croppedSrc = imageSrc;
    this.setState({ image, croppedImageSrc: imageSrc });
  };

  handlePresetChange = ({ defaults, hidden }) => {
    this.setState({
      settings: defaults,
      hidden,
      croppedImageSrc: null,
    });
  };

  handleResetCrop = () => {
    this.setState({ croppedImageSrc: null });
  };

  handleInfoPressed = () => {
    const win = window.open('http://brand.novemberfive.co', '_blank');
    win.focus();
  };

  needsCrop = () => {
    const { resolution } = this.state.settings;

    if (resolution) {
      return true;
    }

    return false;
  };

  imageRef = React.createRef();

  render() {
    const { resolution, ...rest } = this.state.settings;

    const { croppedImageSrc, image, hidden, finalResolution } = this.state;

    const exportResolution = resolution || finalResolution;

    return (
      <ImageDrop onLoadedImage={this.handleImageChange}>
        <Wrap>
          <IconButton
            alt="refresh"
            src="/assets/images/info.png"
            onClick={this.handleInfoPressed}
          />
          <SideBar
            hidden={hidden}
            shaderSettings={{ ...rest }}
            resolution={exportResolution}
            onResetResolution={this.handleResolutionReset}
            onChangeShaderSettings={this.handleShaderSettingsChanged}
            onChangeResolution={this.handleResolutionChange}
            onPresetChange={this.handlePresetChange}
            canSave={
              image !== null && (croppedImageSrc !== null || !this.needsCrop())
            }
            onSave={this.handleSaveClick}
            resetCropDisabled={croppedImageSrc == null}
            onResetCrop={this.handleResetCrop}
          />
          <ViewerWrap>
            {!image && <Title>DRAG AND DROP AN IMAGE</Title>}
            {image &&
              !croppedImageSrc &&
              this.needsCrop() && (
                <ImageCrop
                  imageSrc={image.src}
                  resolution={exportResolution}
                  onChange={this.handleImageCrop}
                />
              )}
            {image &&
              (croppedImageSrc || !this.needsCrop()) && (
                <BrandImageViewerStyled
                  settings={{ ...rest }}
                  image={image}
                  resolution={exportResolution}
                  innerRef={this.imageRef}
                />
              )}
          </ViewerWrap>
        </Wrap>
      </ImageDrop>
    );
  }
}

export default Editor;



// WEBPACK FOOTER //
// /opt/build/repo/src/modules/main/components/Editor/Editor.js